import { useTranslation } from "react-i18next";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { useOtherCustomQuotationTypeOptions } from "@/hooks/useEnumOptions";
import { PackagingTypeBadge } from "@/components/shared/badge/packagingType.badge";
import { InfoAccordionRow, InfoRowNested } from "./infoCard";

/** Packaging type, expandable with the custom quotation content when there is one. */
export const PackagingAccordion = ({
  expedition,
}: {
  expedition: GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const otherCustomQuotationOptions = useOtherCustomQuotationTypeOptions();
  const cq = expedition.customQuotation;
  const otherTypeLabel =
    otherCustomQuotationOptions.find(
      (option) => option.value === cq?.otherDetail?.otherCustomQuotationType,
    )?.label || "-";

  return (
    <InfoAccordionRow
      label={t("expeditionDrawerDetails.packaging")}
      value={<PackagingTypeBadge expedition={expedition} />}
    >
      {cq?.type === CustomQuotationTypeEnum.PALETTE ? (
        <>
          {cq.paletteDetail?.paletteProducts?.map((product, index) => (
            <InfoRowNested
              key={`product-${index}`}
              label={t("expeditionDrawerDetails.productIndex", { index: index + 1 })}
              value={`${product.bottleQuantity} x ${product.volume}L`}
            />
          ))}
          {cq.paletteDetail?.palettePackages?.map((p, index) => (
            <InfoRowNested
              key={`palette-${index}`}
              label={t("expeditionDrawerDetails.paletteIndex", { index: index + 1 })}
              value={`${p.length} x ${p.width} x ${p.height} | ${p.weight} kg | ${p.value} €`}
            />
          ))}
        </>
      ) : cq?.type === CustomQuotationTypeEnum.PARCEL ? (
        <>
          {cq.parcelDetail?.parcelProducts?.map((product, index) => (
            <InfoRowNested
              key={`parcel-product-${index}`}
              label={t("expeditionDrawerDetails.productIndex", { index: index + 1 })}
              value={
                product.type === "BOTTLE"
                  ? `${product.bottleQuantity} x ${product.volume}L`
                  : product.description
              }
            />
          ))}
          {cq.parcelDetail?.parcelPackages?.map((p, index) => (
            <InfoRowNested
              key={`parcel-${index}`}
              label={t("expeditionDrawerDetails.parcelIndex", { index: index + 1 })}
              value={`${p.length} x ${p.width} x ${p.height} | ${p.weight} kg | ${p.value} €`}
            />
          ))}
        </>
      ) : cq?.type === CustomQuotationTypeEnum.OTHER ? (
        <>
          <InfoRowNested label={t("expeditionDrawerDetails.otherType")} value={otherTypeLabel} />
          <InfoRowNested
            label={t("expeditionDrawerDetails.multipleDelivery")}
            value={
              cq.otherDetail?.isMultipleExpedition
                ? t("common:boolean.yes")
                : t("common:boolean.no")
            }
          />
          <InfoRowNested
            label={t("expeditionDrawerDetails.numberOfExpeditions")}
            value={cq.otherDetail?.numberOfExpedition?.toString() || "-"}
          />
        </>
      ) : null}
    </InfoAccordionRow>
  );
};
